import { Component, Vue } from 'vue-facing-decorator';;
import { ElMessage, ElMessageBox } from 'element-plus';
import { Memo, Check, Delete, EditPen, CirclePlusFilled, GoldMedal, Folder } from '@element-plus/icons-vue';
import Header from '@/components/Header/Header.vue';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import TestItem from '@/components/TestItem/TestItem.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import ApiService from '@/services/ApiService';
import AuthService from '@/services/AuthService';

@Component(
{
    components:
    {
        Header,
        Breadcrumbs,
        Navigation,
        TestItem,
		Memo,
		Check,
		EditPen,
        CirclePlusFilled,
		Delete,
        GoldMedal,
        Folder
    },
    enums:
    {
        NavigationIndex
    }
})

export default class Test extends Vue 
{
    private activeName : string = "1";  
    private isCustomer : boolean = true;
    private testModelList : TestModel[] = [];
    private testModelArchiveList : TestModel[] = [];
    private loading : boolean = true;
    private formValid : boolean = false;
    private hasPermission: boolean[] = [] as boolean[];
    private customerModelList : CustomerModel[] | undefined = [] as CustomerModel[];
    private targetGroupList : TargetGroupModel[] = [] as TargetGroupModel[];
    private targetGroupId : string = "Bitte wählen...";
    private toDelete : TestModel | undefined;
    private testDate : Date;
    private newTestModel : TestModel = {} as TestModel;
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;
    private rules =
    {
        customerid:
        [
            {
                required: true,
                message: 'Bitte wählen Sie den Mandanten für diesen Test aus.',
                trigger: 'blur'
            }
        ],
        name:
        [
            {
                required: true,
                message: 'Bitte geben Sie eine Bezeichnung ein.',
                trigger: 'blur'
            }
        ],
        testdate:
        [
            {
                required: true,
                message: 'Bitte geben Sie das Datum des Tests ein.',
                trigger: 'blur'
            }
        ],
        targetgroupid:
        [
            {
                required: true,
                message: 'Bitte geben Sie die Zielgruppe für den Test an.',
                trigger: 'blur'
            }
        ]
    };

    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['TEST_SELECT'] = AuthService.hasPermission('TEST_SELECT');
        this.hasPermission['TEST_INSERT'] = AuthService.hasPermission('TEST_INSERT');
        this.hasPermission['TEST_UPDATE'] = AuthService.hasPermission('TEST_UPDATE');
        this.hasPermission['TEST_DELETE'] = AuthService.hasPermission('TEST_DELETE');
        
        // check if user is a customer
        this.isCustomer = await AuthService.isCustomer();

        if (this.hasPermission['TEST_SELECT'])
        {
            await this.loadCustomers();
            await this.loadList();
            await this.loadArchive();
            await this.loadTargetGroups();
        }

        window.addEventListener('resize', this.onWindowResize);

        this.loading = false;
    }

    private beforeUnmount()
    {
        window.removeEventListener('resize', this.onWindowResize);
    }

	private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private async loadList()
    {
        this.loading = true;
        this.testModelList = await ApiService.getTestsOverview() as TestModel[];
        this.loading = false;
    }

    private async loadArchive()
    {
        this.loading = true;
        this.testModelArchiveList = await ApiService.getTestsArchive() as TestModel[];
        this.loading = false;
    }

    private async loadCustomers()
    {
        this.loading = true;
        this.customerModelList = await ApiService.getAllCustomers() as CustomerModel[];
        this.loading = false;

        if (this.customerModelList && this.customerModelList.length > 0)
        {
            this.newTestModel.customerid = this.customerModelList[0].customerid;
        }
    }

    private async loadTargetGroups()
    {
        this.loading = true;
        this.targetGroupList = await ApiService.getAllTargetGroups() as TargetGroupModel[];
        this.loading = false;
    }

    private async createTest()
    {
        // setup test date
        this.newTestModel.testdate = ApiService.getTimestampForTimezone(this.testDate, "Europe/Berlin") as any;

        // set default values
        this.newTestModel.customerid = this.customerModelList[0].customerid;
        this.newTestModel.complexity = 1;
        this.newTestModel.questioncount = this.getQuestionCount(this.newTestModel.targetgroupid);
        this.newTestModel.timelimit = 120;
        this.newTestModel.successpct = 75;

        // send new test model via request to API and await
        let test = await ApiService.createTest(this.newTestModel);

        // check if successful
        if (test)
        {
			ElMessage(
            {
               	message: "Neue Prüfung erfolgreich erstellt.",
               	type: 'success',
            });

            this.activeName = "1";
            this.newTestModel = {} as TestModel;
            this.testDate = null;
            this.formValid = false;
            await this.loadList();
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Beim Erstellen der Prüfung ist ein Fehler aufgetreten.",
               	type: 'error',
            });
        }
    }

    private async updateTest(test : TestModel)
    {
        let ok : any = await ApiService.updateTest(test);

        if (ok)
        {
			ElMessage(
            {
               	message: "Änderungen wurden übernommen.",
               	type: 'success',
            });
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Änderungen konnten nicht gespeichert werden.",
               	type: 'error',
            });

        }
    }

    private async deleteTest(test : TestModel)
    {
        let ok = await ApiService.deleteTest(test);

        if (ok)
        {
			ElMessage(
            {
               	message: "Prüfung wurde gelöscht.",
               	type: 'success',
            });
            await this.loadList();
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Prüfung konnte nicht gelöscht werden.",
               	type: 'error',
            });
        }
    }

    private getQuestionCount(targetgroupid : number)
    {
        for (let tgm : TargetGroupModel of this.targetGroupList)
        {
            if (tgm.targetgroupid == targetgroupid)
            {
                return tgm.questioncount;
            }
        }
    }

    private validateForm() : boolean
    {
        if (!this.newTestModel.name || this.newTestModel.name.length <= 0) return false;
        if (!this.testDate) return false;
        if (isNaN(this.newTestModel.targetgroupid) || this.newTestModel.targetgroupid <= 0) return false;

        return true;
    }

    private onInput()
    {
        this.formValid = this.validateForm();
    }

    private onTestSaveClick(test : TestModel)
    {
        this.updateTest(test);
    }

    private onDeleteConfirm(action : string)
    {
        if (this.toDelete && action == 'confirm')
        {
            this.deleteTest(this.toDelete);
        }
    }

    private onTestEdit(test : TestModel)
    {
        this.$router.push(`/test/${test.testid}`);
    }

    private onTestDelete(test : TestModel)
    {
        this.toDelete = test;

        ElMessageBox.confirm('Soll diese Prüfung wirklich gelöscht werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onDeleteConfirm,
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        });
    }

    private onNewTestClick()
    {
        this.createTest();
    }
}
